import { Share2 as ExportIcon } from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ActivitySection } from './components/ActivitySection';
import { Button } from './components/Button';
import { MoviesGrid } from './components/Movie/MoviesGrid';
import { FilterAndSorting } from './FilterAndSorting';
import { getRating, isWatchlisted } from './helpers';
import {
  useLatestActivity,
  useMoviesFromPerson,
  useRatingsOrWatchlist,
  useSearch,
  useUpcoming,
} from './hooks/useMovieData';
import { Meta } from './Meta';
import { MovieList } from './MovieList';
import { UserContext } from './UserContext';

export function HomePage({}) {
  const { query, username, listMode, personId, personName } = useParams();
  const userData = useContext(UserContext);
  const location = useLocation();
  const upcoming = location.pathname === '/upcoming';

  const [data, setData] = useState({
    movies: [],
    moviesUnfiltered: [],
    people: [],
    loading: true,
  });
  const [title, setTitle] = useState('Home');

  const region = userData.options?.country_code || '';
  const isRoot = location.pathname === '/';
  const isSearchRoute = Boolean(query);
  const isUserRoute = Boolean(username);
  const isPersonRoute = Boolean(personId);

  const upcomingData = useUpcoming(isRoot || upcoming ? region : '');
  const activityData = useLatestActivity();
  const searchData = useSearch(isSearchRoute ? query : '');
  const ratingsData = useRatingsOrWatchlist(
    isUserRoute ? username : '',
    listMode === 'ratings',
  );
  const personData = useMoviesFromPerson(isPersonRoute ? personId : '');

  const homeUpcoming = upcomingData.movies || [];
  const homeActivity = activityData.activity || null;
  const homeLoading = Boolean(upcomingData.isLoading || activityData.isLoading);

  const updateMovieList = (list) => {
    setData((prev) => ({ ...prev, moviesUnfiltered: list || [] }));
  };

  const handleApplyFiltersAndSorting = (filteredAndSorted) => {
    setData((prev) => {
      const newLoading = prev.moviesUnfiltered.length === 0 && prev.loading;
      return {
        ...prev,
        movies: filteredAndSorted,
        loading: newLoading,
      };
    });
  };

  useEffect(() => {
    if (!isRoot) {
      setData({
        movies: [],
        moviesUnfiltered: [],
        people: [],
        loading: true,
      });
    }
  }, [isRoot, location.pathname]);

  useEffect(() => {
    if (isRoot) {
      setTitle('Home');
    }
  }, [isRoot]);

  useEffect(() => {
    if (isRoot) {
      return;
    }

    if (upcoming) {
      setTitle('Upcoming');
      updateMovieList(upcomingData.movies || []);
      setData((prev) => ({
        ...prev,
        people: [],
        loading: Boolean(upcomingData.isLoading),
      }));
      return;
    }

    if (isSearchRoute) {
      setTitle(`Search ${query}`);
      const searchResults = searchData.results || {};
      setData((prev) => ({
        ...prev,
        moviesUnfiltered: searchResults.movie || [],
        people: searchResults.person || [],
        loading: Boolean(searchData.isLoading),
      }));
      return;
    }

    if (isUserRoute) {
      setTitle(
        `${username === userData.username ? 'My' : `${username}'s`} ${listMode === 'ratings' ? 'ratings' : 'watchlist'}`,
      );
      updateMovieList(ratingsData.movies || []);
      setData((prev) => ({
        ...prev,
        people: [],
        loading: Boolean(ratingsData.isLoading),
      }));
      return;
    }

    if (isPersonRoute) {
      setTitle(`Movies with ${personName}`);
      updateMovieList(personData.movies || []);
      setData((prev) => ({
        ...prev,
        people: [],
        loading: Boolean(personData.isLoading),
      }));
    }
  }, [
    isRoot,
    upcoming,
    isSearchRoute,
    isUserRoute,
    isPersonRoute,
    query,
    username,
    personName,
    listMode,
    userData.username,
    upcomingData.movies,
    upcomingData.isLoading,
    searchData.results,
    searchData.isLoading,
    ratingsData.movies,
    ratingsData.isLoading,
    personData.movies,
    personData.isLoading,
  ]);

  return (
    <div style={{ position: 'relative' }}>
      <Meta subtitle={title} />

      {isRoot ? (
        // Root route: two-column layout with upcoming and recent activity
        <div className="home-page-layout">
          {/* Recent Activity Section */}
          <div className="home-page-section">
            <ActivitySection activities={homeActivity} loading={homeLoading} />
          </div>
          {/* Upcoming Section */}
          <div className="home-page-section">
            <div className="listing-info">
              <span className="listing-info__title">Upcoming</span>
            </div>
            <MoviesGrid
              movies={homeUpcoming}
              showContent={!homeLoading}
              size="compact"
              static
            />
          </div>
        </div>
      ) : (
        // Regular route: standard list view
        <>
          <div className="listing-header">
            <FilterAndSorting
              data={data.moviesUnfiltered}
              loading={data.loading}
              onApply={handleApplyFiltersAndSorting}
            />
            <div className="listing-info">
              <span className="listing-info__title">{title}</span>
              {data.loading ? null : (
                <span className="listing-info__count">{`(${data.movies.length}/${data.moviesUnfiltered.length})`}</span>
              )}
              <Button
                className="listing-info__export"
                title="Export this view as CSV"
                onClick={() => {
                  const csvData = data.movies.reduce((s, m) => {
                    return `${s}${m.id},${m.imdb_id},"${m.title},${m.release_year}",${
                      getRating(m, Number(userData.id)) ?? null
                    },${isWatchlisted(m, Number(userData.id))}\n`;
                  }, 'id,imdbID,title,year,rating,watchlisted\n');

                  const blob = new Blob([csvData], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);

                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${title}.txt`;
                  a.click();
                }}
              >
                <ExportIcon size={16} />
                Export
              </Button>
            </div>
          </div>
          <MovieList
            movies={data.movies}
            people={data.people}
            loading={data.loading}
          />
        </>
      )}
    </div>
  );
}
